/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  FormLabel,
  CodeSnippet,
  CodeSnippetSkeleton,
  InlineNotification
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMount } from 'react-use';

import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppSelector } from '@ibm-watson/shared/store';
import { ApiStatus } from '../../../../ts';
import { useChannelHelpers } from '../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../hooks/useFormHelpers';
import {
  didSubscribeIntegrationFail,
  didSubscribeIntegrationSucceed,
  selectDraftIntegration,
  selectSubscribeStatus
} from '../../store/channels/selectors';
import {
  INTELEPEER_SMS_TYPE,
  SMS_CHANNEL_TYPE,
  TWILIO_SMS_TYPE,
  WHATSAPP_CHANNEL_TYPE,
  GENESYS_SMS_TYPE,
  CUSTOM_SMS_TYPE
} from '../../util/channelTypes';
import { QuickSetupComponentProps } from '../../util/types';
import { TWILIO_COLOR_PRIMARY } from '../../util/validation/constants';
import { GenesysIcon } from '../icons/GenesysIcon';
import { IntelepeerIcon } from '../icons/IntelepeerIcon';
import { TwilioIcon } from '../icons/TwilioIcon';
import { sharedMessages } from '../messages';
import {
  TwilioMessagingWebhookStepMessages,
  WhatsAppWebhookStepMessages,
  WebhookStepMessages as messages
} from './messages';

interface WebhookStepProps extends QuickSetupComponentProps {
  /**
   * The type of channel for the webhook (e.g., SMS, Whatsapp etc.)
   * This helps determine the channel-specific configurations.
   */
  channelType?: string;
  /**
   * The name or identifier of the provider's field, if applicable.
   * This is used to map the provider's field in the configuration.
   */
  providerField?: string;
  /**
   * The Account SID for authentication.
   * This is required for setting up the Twilio integration with the webhook.
   */
  account_sid: string;
  /**
   * The Account Secret, which is optional.
   * This can be used in case you need additional authentication information for Intelepeer.
   */
  account_secret?: string;
  /**
   * The Twilio Auth Token for authentication.
   * This is required to authenticate API requests to Twilio.
   */
  twilio_auth_token: string;
  /**
   * The IntelePeer authentication token, if applicable.
   * This token is needed to authenticate the webhook with IntelePeer services.
   */
  intelepeer_auth_token?: string;
  /**
   * The Genesys Client ID, used for Genesys SMS type.
   * This is required to connect the webhook with Genesys services.
   */
  genesys_client_id?: string;
  /**
   * The Genesys Client Secret, used for Genesys authentication.
   * This is optional, but required when making API calls to Genesys services.
   */
  genesys_client_secret?: string;
  /**
   * The Genesys Verification Token, which is optional.
   * This token is used for webhook validation to ensure the webhook request is coming from a trusted source.
   */
  genesys_verification_token?: string;
  /**
   * The BYOP(Bring your own provider) Verification Token, which is optional.
   * This token is used for webhook validation to ensure the webhook request is coming from a trusted source.
   */
  byop_verification_token?: string;
  /**
   * The URL for the mobile messaging provider, if needed.
   * This URL is used to send messages via the specified provider.
   */
  mobile_messaging_provider_url?: string;
  /**
   * A phone number associated with the webhook, if necessary.
   * This number is used for communication purposes (e.g., sending or receiving SMS).
   */
  phone_number?: string;
  /**
   * The URL to which the webhook will send requests.
   * This is the endpoint where the webhook will send data.
   */
  webhook_url: string;
  /**
   * A flag to determine if a logo should be included in the setup.
   */
  includeLogo?: boolean;
}
export const WebhookStep = ({
  assistantId,
  environmentId,
  markStepComplete,
  channelType,
  providerField,
  account_sid,
  account_secret,
  twilio_auth_token,
  intelepeer_auth_token,
  genesys_client_id,
  genesys_client_secret,
  genesys_verification_token,
  byop_verification_token,
  mobile_messaging_provider_url,
  phone_number,
  webhook_url,
  includeLogo
}: WebhookStepProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const subscribeFailed = useAppSelector(didSubscribeIntegrationFail);
  const subscribeStatus = useAppSelector(selectSubscribeStatus);
  const subscribeIdleOrLoading =
    subscribeStatus === ApiStatus.IDLE || subscribeStatus === ApiStatus.LOADING;
  const subscribeSucceeded = useAppSelector(didSubscribeIntegrationSucceed);

  const channelOrProvider =
    channelType === WHATSAPP_CHANNEL_TYPE
      ? WHATSAPP_CHANNEL_TYPE
      : getFieldValue(providerField);

  const WebhookStepMessages =
    draftIntegration?.type === WHATSAPP_CHANNEL_TYPE
      ? WhatsAppWebhookStepMessages
      : TwilioMessagingWebhookStepMessages;

  // When field is valid, we need to mark this step complete...
  useEffect(() => {
    markStepComplete(subscribeSucceeded ?? false);
  }, [markStepComplete, subscribeSucceeded]);

  // Call webhook subscribe on mount...
  useMount(() => {
    if (!_.isNil(assistantId)) {
      const data = _.isEqual(getFieldValue(providerField), INTELEPEER_SMS_TYPE)
        ? {
            account_secret: getFieldValue(account_secret),
            auth_token: getFieldValue(intelepeer_auth_token),
            phone_number: getFieldValue(phone_number)
          }
        : _.isEqual(getFieldValue(providerField), CUSTOM_SMS_TYPE)
        ? {
            verification_token: getFieldValue(byop_verification_token),
            api_url: getFieldValue(mobile_messaging_provider_url)
          }
        : _.isEqual(getFieldValue(providerField), TWILIO_SMS_TYPE) ||
          channelType === WHATSAPP_CHANNEL_TYPE
        ? {
            account_sid: getFieldValue(account_sid),
            auth_token: getFieldValue(twilio_auth_token)
          }
        : {
            client_id: getFieldValue(genesys_client_id),
            client_secret: getFieldValue(genesys_client_secret),
            verification_token: getFieldValue(genesys_verification_token),
            api_url: getFieldValue(mobile_messaging_provider_url)
          };

      subscribeForWebhookUrl(
        assistantId,
        environmentId,
        draftIntegration,
        data,
        webhook_url,
        getFieldValue(providerField)
      );
    }
  });

  return (
    <div className="TabContent__body-content TabContent__body-outdent">
      <div className="QuickSetup__step-title">
        <FormattedMessage {...messages.title} />
      </div>
      <div className="TabContent__spacer8" />
      <div className="TabContent__subtitle">
        <FormattedMessage {...WebhookStepMessages.description} />
      </div>
      <div className="TabContent__spacer16" />
      {includeLogo && channelOrProvider === TWILIO_SMS_TYPE && (
        <>
          <TwilioIcon
            className="ServiceDesk--twilio"
            channelType={SMS_CHANNEL_TYPE}
            fill={TWILIO_COLOR_PRIMARY}
          />
          <div className="TabContent__spacer8" />
        </>
      )}
      {includeLogo && channelOrProvider === INTELEPEER_SMS_TYPE && (
        <>
          <IntelepeerIcon className="ServiceDesk--intelepeer" />
          <div className="TabContent__spacer16" />
        </>
      )}
      {includeLogo && channelOrProvider === GENESYS_SMS_TYPE && (
        <>
          <GenesysIcon className="ServiceDesk--genesys" />
          <div className="TabContent__spacer16" />
        </>
      )}
      {!subscribeFailed && (
        <FormLabel className="TabContent__field-label">
          <FormattedMessage {...WebhookStepMessages.webhookLabel} />
        </FormLabel>
      )}
      {subscribeIdleOrLoading && <CodeSnippetSkeleton />}
      {subscribeSucceeded && getFieldValue(webhook_url) && (
        <CodeSnippet
          copyButtonDescription={t(sharedMessages.copyToClipboard)}
          copyLabel={t(sharedMessages.copyToClipboard)}
          feedback={t(sharedMessages.copied)}
          onClick={() => {
            copyContentToClipboard(getFieldValue(webhook_url));
          }}
          type="single">
          {getFieldValue(webhook_url)}
        </CodeSnippet>
      )}
      {subscribeFailed &&
        (channelOrProvider === TWILIO_SMS_TYPE ||
          channelOrProvider === WHATSAPP_CHANNEL_TYPE) && (
          <InlineNotification
            kind="error"
            title={t(messages.webhookErrorTitleTwilio)}
            subtitle={t(messages.webhookErrorSubtitleTwilio)}
            hideCloseButton={true}
          />
        )}
      {subscribeFailed && channelOrProvider === INTELEPEER_SMS_TYPE && (
        <InlineNotification
          kind="error"
          title={t(messages.webhookErrorTitleIntelepeer)}
          subtitle={t(messages.webhookErrorSubtitleIntelepeer)}
          hideCloseButton={true}
        />
      )}
      {subscribeFailed && channelOrProvider === GENESYS_SMS_TYPE && (
        <InlineNotification
          kind="error"
          title={t(messages.webhookErrorTitleGenesys)}
          subtitle={t(messages.webhookErrorSubtitleGenesys)}
          hideCloseButton={true}
        />
      )}
      {subscribeFailed && channelOrProvider === CUSTOM_SMS_TYPE && (
        <InlineNotification
          kind="error"
          title={t(messages.webhookErrorTitleByop)}
          subtitle={t(messages.webhookErrorSubtitleByop)}
          hideCloseButton={true}
        />
      )}
    </div>
  );
};
